import classNames from "classnames";
import { memo, type ReactNode } from "react";

import * as styles from "./segmented-toggle.module.css";

interface SegmentedToggleOption<T extends string> {
  value: T;
  label: ReactNode;
  disabled?: boolean;
}

interface SegmentedToggleProps<T extends string> {
  options: readonly SegmentedToggleOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /**
   * ARIA role for the wrapper. Defaults to `radiogroup` for form-style
   * usage; pass `tablist` when the toggle drives view switching.
   */
  role?: "radiogroup" | "tablist";
  ariaLabel?: string;
}

/**
 * Pill-style segmented control. Visual treatment matches the date-period
 * selector on `/onboardings/all/merchants` (lifted from
 * `returns-page.module.css`).
 */
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const SegmentedToggle = memo(function SegmentedToggle<T extends string>({
  options,
  value,
  onChange,
  role = "radiogroup",
  ariaLabel,
}: SegmentedToggleProps<T>) {
  const isTablist = role === "tablist";
  return (
    <div aria-label={ariaLabel} className={styles.pill} role={role}>
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            aria-checked={isTablist ? undefined : active}
            aria-selected={isTablist ? active : undefined}
            className={classNames(styles.option, active && styles.optionActive)}
            disabled={opt.disabled}
            key={opt.value}
            onClick={() => onChange(opt.value)}
            role={isTablist ? "tab" : "radio"}
            type="button"
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}) as <T extends string>(props: SegmentedToggleProps<T>) => JSX.Element;
